// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// gen.ts — tonight's building, from a seed.
//
// THE GENERATOR IS NEVER BELIEVED. It proposes; verify.ts disposes. "Unreachable by construction" is a
// hypothesis, and this fleet has already measured a generator's own solvability argument wrong on 3.2%
// of the floors it was confident about. So the loop here is propose → verify → reroll, and the thing
// that decides is a module that shares none of this one's reasoning.
//
// LOOPS, NOT CORRIDORS. The doorway graph is a spanning tree PLUS thirty per cent of the remaining
// adjacent-room edges, because a dead end in a stealth game is a sentence: if there is only one way
// out of a room, then a body in the doorway is not a problem to solve, it is a loss to sit through.
// The cyclomatic number of the room graph is an enforced interest metric for exactly that reason.
//
// PATROL LOOPS ARE 4, 6, 8 OR 12 AND THE LENSES RUN ON 8. lcm(4,6,8,12,8) = 24, so at CALM the entire
// world is periodic with period 24 — which is what lets verify.ts search (cell, phase, taken) exactly
// rather than approximately, and what makes the dashed next-turn arc on screen a true statement rather
// than a guess.

import { makeRng } from '@ben-gy/game-engine/rng';
import {
  COLS,
  CELLS,
  CYCLOMATIC_MIN,
  Floor,
  LOOP_LENGTHS,
  MAX_REROLLS,
  MAX_ROOMS,
  MIN_ROOMS,
  EXTRA_DOOR_PCT,
  ROWS,
} from './tuning';
import { DoorState, canStep, idx, neighbours, walkField, makeGrid, xOf, yOf, type Door, type Grid } from './map';
import type { Building, Item } from './rules';
import type { Mode } from './modes';
import { verify, type Verdict } from './verify';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type Rand = () => number;
const pick = <T>(r: Rand, xs: readonly T[]): T => xs[Math.floor(r() * xs.length) % xs.length];
const randInt = (r: Rand, lo: number, hi: number): number => lo + Math.floor(r() * (hi - lo + 1));

/**
 * Binary space partition. Rooms are separated by a one-cell wall, and doorways are punched through
 * that wall afterwards.
 *
 * A room is at least MIN_SIDE on each side, so a rect can only be cut if it can afford two of those
 * plus the wall between them — hence SPLITTABLE = 2 * MIN_SIDE + 1. On an eight-by-ten floor that is
 * a tight budget and the thresholds have to be exactly right: the first version demanded a side of
 * seven before it would cut anything, which on this board meant the very first split was usually the
 * last, every candidate came back under five rooms, and the generator returned null for every seed it
 * was ever given.
 */
const MIN_SIDE = 2;
const SPLITTABLE = MIN_SIDE * 2 + 1;

export function partition(r: Rand, want: number): Rect[] {
  let rects: Rect[] = [{ x: 0, y: 0, w: COLS, h: ROWS }];
  for (let guard = 0; guard < 60 && rects.length < want; guard++) {
    // Largest first, so a floor is rooms rather than one hall and a few cupboards. A rect that cannot
    // be cut is skipped rather than ending the whole partition — some smaller sibling may still be
    // long enough in the other axis.
    const order = rects
      .map((q, i) => ({ q, i }))
      .sort((a, b) => b.q.w * b.q.h - a.q.w * a.q.h || a.q.y * COLS + a.q.x - (b.q.y * COLS + b.q.x));
    let cutOne = false;
    for (const { q: t, i } of order) {
      const canV = t.w >= SPLITTABLE;
      const canH = t.h >= SPLITTABLE;
      if (!canV && !canH) continue;
      rects.splice(i, 1);
      const vertical = canV && (!canH || r() < 0.5);
      if (vertical) {
        const cut = randInt(r, MIN_SIDE, t.w - MIN_SIDE - 1);
        rects.push({ x: t.x, y: t.y, w: cut, h: t.h });
        rects.push({ x: t.x + cut + 1, y: t.y, w: t.w - cut - 1, h: t.h });
      } else {
        const cut = randInt(r, MIN_SIDE, t.h - MIN_SIDE - 1);
        rects.push({ x: t.x, y: t.y, w: t.w, h: cut });
        rects.push({ x: t.x, y: t.y + cut + 1, w: t.w, h: t.h - cut - 1 });
      }
      cutOne = true;
      break;
    }
    if (!cutOne) break;
  }
  rects = rects.filter((q) => q.w >= MIN_SIDE && q.h >= MIN_SIDE);
  rects.sort((a, b) => a.y * COLS + a.x - (b.y * COLS + b.x));
  return rects;
}

/** Every pair of rooms separated by exactly one wall cell, with the wall cells between them. */
function adjacencies(roomOf: Int8Array): Map<string, number[]> {
  const out = new Map<string, number[]>();
  for (let c = 0; c < CELLS; c++) {
    if (roomOf[c] >= 0) continue;
    const x = xOf(c);
    const y = yOf(c);
    const pairs: [number, number][] = [];
    if (x > 0 && x < COLS - 1) pairs.push([roomOf[c - 1], roomOf[c + 1]]);
    if (y > 0 && y < ROWS - 1) pairs.push([roomOf[c - COLS], roomOf[c + COLS]]);
    for (const [a, b] of pairs) {
      if (a < 0 || b < 0 || a === b) continue;
      const key = a < b ? `${a}-${b}` : `${b}-${a}`;
      const list = out.get(key) ?? [];
      list.push(c);
      out.set(key, list);
    }
  }
  return out;
}

export function buildOnce(seed: number, mode: Mode): Building | null {
  const r = makeRng(seed);
  const g = makeGrid();
  g.wall.fill(1);

  const rooms = partition(r, randInt(r, MIN_ROOMS, MAX_ROOMS));
  if (rooms.length < MIN_ROOMS) return null;
  for (let i = 0; i < rooms.length; i++) {
    const q = rooms[i];
    for (let y = q.y; y < q.y + q.h; y++) {
      for (let x = q.x; x < q.x + q.w; x++) {
        const c = idx(x, y);
        g.wall[c] = 0;
        g.room[c] = i;
      }
    }
  }
  g.roomCount = rooms.length;

  // Floor types per room. At least one room in four is hard flooring, and the whole-floor share is
  // checked afterwards — a building that is entirely carpet makes the movement half of the hearing
  // system inert, and nothing else in the pipeline would ever notice.
  for (let i = 0; i < rooms.length; i++) {
    const t = r() < 0.34 ? Floor.Grating : r() < 0.6 ? Floor.Tile : Floor.Carpet;
    const q = rooms[i];
    for (let y = q.y; y < q.y + q.h; y++) for (let x = q.x; x < q.x + q.w; x++) g.floorT[idx(x, y)] = t;
  }

  // Doorways: a spanning tree over the room graph, plus a share of the remaining edges as loops.
  const adj = adjacencies(g.room);
  const keys = [...adj.keys()].sort();
  const parent = new Int8Array(rooms.length).map((_, i) => i);
  const find = (a: number): number => {
    while (parent[a] !== a) a = parent[a] = parent[parent[a]];
    return a;
  };
  const doors: Door[] = [];
  const punch = (cell: number): boolean => {
    if (g.doorAt[cell] >= 0) return false;
    g.wall[cell] = 0;
    g.floorT[cell] = Floor.Tile;
    g.doorAt[cell] = doors.length;
    doors.push({ cell, state: DoorState.Closed, autoLocked: false });
    return true;
  };
  const spare: string[] = [];
  for (const key of keys) {
    const [a, b] = key.split('-').map(Number);
    if (find(a) === find(b)) {
      spare.push(key);
      continue;
    }
    parent[find(a)] = find(b);
    punch(adj.get(key)![Math.floor(r() * adj.get(key)!.length) % adj.get(key)!.length]);
  }
  for (const a of rooms.keys()) if (find(a) !== find(0)) return null; // disconnected floor plan

  // LOOPS ARE A REQUIREMENT, NOT A FLAVOUR, so a minimum is guaranteed rather than rolled for.
  // A spanning tree has cyclomatic number zero — every room is a cul-de-sac — and on this board there
  // are only ever two or three spare edges to begin with, so a flat thirty-per-cent chance produced no
  // extra door at all on most seeds and the verifier correctly threw away five buildings in six.
  const wantExtra = Math.max(CYCLOMATIC_MIN, Math.round((spare.length * EXTRA_DOOR_PCT) / 100));
  let extra = 0;
  for (const key of spare) {
    if (extra >= wantExtra) break;
    const cells = adj.get(key)!;
    if (punch(cells[Math.floor(r() * cells.length) % cells.length])) extra++;
  }

  const openCells: number[] = [];
  for (let c = 0; c < CELLS; c++) if (!g.wall[c] && g.doorAt[c] < 0) openCells.push(c);
  if (openCells.length < 30) return null;

  // Entry sits on the bottom edge — the front door — so the opening beat happens where the thumb is.
  const bottom = openCells.filter((c) => yOf(c) >= ROWS - 2);
  if (bottom.length === 0) return null;
  const entry = pick(r, bottom);

  // Placement asks whether the floor is CONNECTED, not whether it is connected right this second, so
  // it floods with every door open. Doors start closed; flooding with their starting state confines
  // the whole search to the entry room, which silently rejected every seed the generator ever saw.
  const openDoors: Door[] = doors.map((d) => ({ ...d, state: DoorState.Open }));
  const field = walkField(g, openDoors, entry);
  const reach = openCells.filter((c) => field[c] >= 0);
  if (reach.length < 28) return null;
  const far = [...reach].sort((a, b) => field[b] - field[a] || a - b);

  // Two duct mouths in opposite halves. The duct is the floor's only topology in place of stairs.
  const top = reach.filter((c) => yOf(c) < ROWS / 2);
  const bot = reach.filter((c) => yOf(c) >= ROWS / 2 && c !== entry);
  if (!top.length || !bot.length) return null;
  const ducts: [number, number] = [pick(r, bot), pick(r, top)];
  if (ducts[0] === ducts[1]) return null;
  g.ductAt[ducts[0]] = 0;
  g.ductAt[ducts[1]] = 1;

  // Exits: the front door you came in by (sealed at LOCKDOWN), a loading dock, and the roof duct.
  const dock = far.find((c) => c !== entry && yOf(c) >= ROWS - 3 && c !== ducts[0] && c !== ducts[1]);
  const exits = [entry, dock ?? entry, ducts[1]].filter((c, i, a) => a.indexOf(c) === i);

  const used = new Set<number>([entry, ...exits, ...ducts]);
  const take = (pool: number[]): number => {
    for (const c of pool) if (!used.has(c)) return (used.add(c), c);
    return -1;
  };

  const chutes = [take(bot.slice().sort((a, b) => a - b)), take(top.slice().sort((a, b) => b - a))].filter(
    (c) => c >= 0,
  );
  if (chutes.length < 2) return null;

  const strongroom = take(far);
  if (strongroom < 0) return null;

  // One breaker per room, on a cell inside it.
  const breakers: number[] = [];
  for (let i = 0; i < rooms.length; i++) {
    const inRoom = reach.filter((c) => g.room[c] === i && !used.has(c)).sort((a, b) => a - b);
    const b = inRoom.length ? inRoom[Math.floor(r() * inRoom.length) % inRoom.length] : -1;
    if (b >= 0) used.add(b);
    breakers.push(b);
  }

  // Loot: value scales with distance from the entry, so the far cases are the greedy ones — and the
  // ENTRY room is guaranteed a mid-value case within reach, which is what makes the first sixty
  // seconds of every seed one completed sneak-past and one grab rather than a walk.
  const values = mode.loot === 0 ? [mode.objectiveValue] : [10, 15, 15, 20, 25].slice(0, mode.loot);
  const items: Item[] = [];
  if (mode.loot === 0) {
    const c = strongroom === -1 ? take(far) : far.find((x) => !used.has(x)) ?? -1;
    if (c < 0) return null;
    used.add(c);
    items.push({ id: 0, cell: c, value: mode.objectiveValue, taken: false, banked: false, confiscated: false });
  } else {
    const near = reach.filter((c) => field[c] > 0 && field[c] <= 6 && !used.has(c)).sort((a, b) => a - b);
    const ordered = [...reach].filter((c) => !used.has(c)).sort((a, b) => field[a] - field[b] || a - b);
    const seedCell = near.length ? near[Math.floor(r() * near.length) % near.length] : -1;
    if (seedCell < 0) return null;
    used.add(seedCell);
    // The 15-value case is the one placed near the door.
    items.push({ id: 0, cell: seedCell, value: 15, taken: false, banked: false, confiscated: false });
    const rest = values.filter((_, i) => i !== 1);
    const step = Math.max(1, Math.floor(ordered.length / (rest.length + 1)));
    for (let i = 0; i < rest.length; i++) {
      const pool = ordered.slice(i * step).filter((c) => !used.has(c));
      const c = pool.length ? pool[Math.floor(r() * Math.min(pool.length, 6)) % pool.length] : -1;
      if (c < 0) return null;
      used.add(c);
      items.push({ id: items.length, cell: c, value: rest[i], taken: false, banked: false, confiscated: false });
    }
  }

  // Patrol loops, of a length in {4,6,8,12}, walked as a closed circuit of waypoints.
  const minderStarts: { cell: number; loop: number[] }[] = [];
  const spread = reach.filter((c) => !used.has(c)).sort((a, b) => a - b);
  for (let i = 0; i < 5; i++) {
    const start = spread[Math.floor((spread.length * (i + 1)) / 6) % spread.length];
    const len = pick(r, LOOP_LENGTHS);
    const loop = walkLoop(g, doors, start, len, r);
    if (loop.length < 2) return null;
    minderStarts.push({ cell: start, loop });
  }

  const lensStarts: { cell: number; facing0: number }[] = [];
  for (let i = 0; i < 2; i++) {
    const c = far[Math.floor((far.length * (i + 2)) / 5) % far.length];
    lensStarts.push({ cell: c, facing0: randInt(r, 0, 7) });
  }

  const anchors = [reach[0], reach[Math.floor(reach.length / 2)], reach[reach.length - 1]];

  return {
    grid: g,
    doors,
    entry,
    exits,
    chutes,
    ducts,
    breakers,
    items,
    minderStarts,
    lensStarts,
    strongroom,
    anchors,
  };
}

/** A closed circuit of `len` waypoints starting at `start`, biased away from where it has just been. */
function walkLoop(g: Grid, doors: readonly Door[], start: number, len: number, r: Rand): number[] {
  const loop: number[] = [];
  let cur = start;
  let prev = -1;
  const nb: number[] = [];
  for (let i = 0; i < len; i++) {
    neighbours(cur, nb);
    const opts = nb.filter((n) => canStep(g, doors, n) && n !== prev).sort((a, b) => a - b);
    if (!opts.length) break;
    const next = opts[Math.floor(r() * opts.length) % opts.length];
    prev = cur;
    cur = next;
    loop.push(cur);
  }
  return loop;
}

export interface Floorplan {
  b: Building;
  verdict: Verdict;
  seed: number;
  /** How many candidates were thrown away before this one. Reported honestly on the run-start card. */
  rerolls: number;
}

/**
 * Tonight's building: propose, verify, reroll. The verdict travels with it, because par and the
 * forced-risk count are produced BY the verification pass rather than estimated afterwards.
 */
export function generate(seed: number, mode: Mode): Floorplan | null {
  for (let attempt = 0; attempt < MAX_REROLLS; attempt++) {
    const b = buildOnce(seed + attempt * 7919, mode);
    if (!b) continue;
    const verdict = verify(b, mode);
    if (verdict.ok) return { b, verdict, seed, rerolls: attempt };
  }
  // Last resort: drop a minder and try once more. A building nobody can rob is worse than an easy one.
  for (let attempt = 0; attempt < MAX_REROLLS; attempt++) {
    const b = buildOnce(seed + attempt * 7919, mode);
    if (!b) continue;
    const easier = { ...mode, minders: Math.max(1, mode.minders - 1) };
    const verdict = verify(b, easier);
    if (verdict.ok) return { b, verdict, seed, rerolls: MAX_REROLLS + attempt };
  }
  return null;
}
